import React, { useLayoutEffect, useRef, useState } from "react";
import { GraphicSVG } from "../lib/graphics";
import { fileUrl } from "../lib/api";
import { buildBaseSvg, buildTurfTextureSvg, turfMaterialFilterSvg } from "../lib/basestyles";
import { computeTrack } from "../lib/track";

function ObjectContent({ obj }) {
  if (obj.type === "logo") {
    if (!obj.fileId && !obj.url)
      return <div className="w-full h-full flex items-center justify-center border border-dashed border-white/50 text-white/60 text-[10px] font-display uppercase tracking-wide pointer-events-none">Your Logo</div>;
    return <img src={obj.url || fileUrl(obj.fileId)} alt="logo" draggable={false} className="w-full h-full object-contain pointer-events-none select-none" />;
  }
  if (obj.type === "text")
    return <div className="w-full h-full flex items-center justify-center leading-none text-center pointer-events-none" style={{ color: obj.colour, fontFamily: `${obj.fontFamily || "Anton"}, sans-serif`, fontSize: `min(${obj.scale * 900}px, 90cqw)`, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.03em" }}>{obj.text}</div>;
  return <div className="w-full h-full pointer-events-none"><GraphicSVG kind={obj.kind} colour={obj.colour} /></div>;
}

// Pointer-down preventDefault alone does not stop the browser placing a collapsed text selection (the
// "flashing caret"); mousedown must be cancelled too and any existing selection/focus dropped.
const noCaret = (e) => e.preventDefault();
function clearCaret() {
  const a = document.activeElement;
  if (a && a !== document.body) a.blur();
  const s = window.getSelection && window.getSelection();
  if (s && s.rangeCount) s.removeAllRanges();
}

function TrackLayer({ design, W, H, material, selectedIds, setSelectedId, toggleSelected, readOnly }) {
  const prims = computeTrack(design, W, H);
  const pick = (e, key) => {
    if (readOnly || !setSelectedId) return;
    e.stopPropagation(); e.preventDefault();          // select only — never place a text caret / selection
    clearCaret();
    if (e.shiftKey && toggleSelected) toggleSelected(`track:${key}`); else setSelectedId(`track:${key}`);
  };
  const hit = (key) => ({
    pointerEvents: readOnly ? "none" : "auto", cursor: readOnly ? "default" : "pointer", userSelect: "none", caretColor: "transparent",
    outline: selectedIds?.includes(`track:${key}`) ? "2px solid #00D26A" : "none", outlineOffset: 3,
  });
  return (
    <div className="absolute inset-0 pointer-events-none z-20" data-testid="track-layer" style={{ filter: material }}>
      {(design.track?.lanes || (design.track?.layout === "two_lane" ? 2 : 1)) >= 2 && <span data-testid="centre-divider" className="hidden" />}
      {prims.map((p) => p.type === "rect" ? (
        <div key={p.key} data-testid={`track-el-${p.key}`} data-sel-id={`track:${p.key}`} onPointerDown={(e) => pick(e, p.key)} onMouseDown={noCaret} className="absolute" style={{ left: p.x, top: p.y, width: p.w, height: p.h, background: p.fill, ...hit(p.key) }} />
      ) : (
        <div key={p.key} data-testid={`track-el-${p.key}`} data-sel-id={`track:${p.key}`} onPointerDown={(e) => pick(e, p.key)} onMouseDown={noCaret} className="absolute font-display font-extrabold" style={{ left: p.x, top: p.y, transform: `translate(-50%,-50%) rotate(${p.rotate || 0}deg)`, color: p.fill, fontSize: p.size, lineHeight: 1, whiteSpace: "nowrap", ...hit(p.key) }}>{p.text}</div>
      ))}
    </div>
  );
}

export default function TurfCanvas({ design, updateObject, checkpoint, selectedId, setSelectedId, selectedIds, setSelectedIds, toggleSelected, showSafe = true, readOnly = false, zoom = 1 }) {
  const selSet = selectedIds || (selectedId ? [selectedId] : []);
  const [marquee, setMarquee] = useState(null);
  const wrapRef = useRef(null);
  const turfRef = useRef(null);
  const [box, setBox] = useState({ w: 800, h: 400 });
  const [guides, setGuides] = useState({ v: false, h: false });
  const gesture = useRef(null);
  const matId = useRef("tm" + Math.random().toString(36).slice(2, 9)).current;

  useLayoutEffect(() => {
    const measure = () => { if (wrapRef.current) { const r = wrapRef.current.getBoundingClientRect(); setBox({ w: r.width, h: r.height }); } };
    measure();
    const ro = new ResizeObserver(measure); if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  const L = design.dimensions.length, W = design.dimensions.width;
  const ratio = L / W;
  const availW = Math.max(120, box.w - 112), availH = Math.max(120, box.h - 112);
  let turfW = availW, turfH = turfW / ratio;
  if (turfH > availH) { turfH = availH; turfW = turfH * ratio; }
  turfW *= zoom; turfH *= zoom;

  const dims = (o) => {
    const w = o.scale * turfW;
    const aspect = o.aspect || (o.type === "graphic" ? 2 : o.type === "text" ? 4 : 1);
    const h = o.type === "text" ? w / (o.aspect || 4) : w / aspect;
    return { w, h };
  };

  const onDown = (e, o, mode) => {
    if (readOnly) return;
    e.stopPropagation(); e.preventDefault();          // select/drag only — never place a text caret / selection
    clearCaret();
    if (e.shiftKey && mode === "drag" && toggleSelected) { toggleSelected(o.id); return; }
    if (!selSet.includes(o.id) || selSet.length === 1) setSelectedId(o.id);
    const rect = turfRef.current.getBoundingClientRect();
    gesture.current = { id: o.id, mode, sx: e.clientX, sy: e.clientY, ox: o.x, oy: o.y, os: o.scale, rw: rect.width, rh: rect.height, moved: false };
    try { e.target.setPointerCapture(e.pointerId); } catch (err) {}
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const onMove = (e) => {
    const g = gesture.current; if (!g) return;
    if (!g.moved) { g.moved = true; if (checkpoint) checkpoint(); }   // one undo entry per gesture
    if (g.mode === "drag") {
      let cx = g.ox + (e.clientX - g.sx) / g.rw, cy = g.oy + (e.clientY - g.sy) / g.rh;
      const sv = Math.abs(cx - 0.5) < 0.02, sh = Math.abs(cy - 0.5) < 0.02;
      if (sv) cx = 0.5; if (sh) cy = 0.5;
      setGuides({ v: sv, h: sh });
      updateObject(g.id, { x: Math.max(0.02, Math.min(0.98, cx)), y: Math.max(0.02, Math.min(0.98, cy)) }, false);
    } else {
      updateObject(g.id, { scale: Math.max(0.03, Math.min(1.3, g.os + (e.clientX - g.sx) / g.rw)) }, false);
    }
  };
  const onUp = () => {
    const g = gesture.current; gesture.current = null;
    setGuides({ v: false, h: false });
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  // Marquee / box selection: starts on EMPTY turf and selects every editable element ([data-sel-id]) intersecting the box.
  const startMarquee = (e) => {
    if (readOnly || !setSelectedIds || e.button !== 0) return;
    const r = turfRef.current.getBoundingClientRect();
    const start = { x: e.clientX - r.left, y: e.clientY - r.top };
    const base = e.shiftKey ? [...selSet] : [];
    if (!e.shiftKey) setSelectedIds([]);
    const move = (ev) => {
      const cur = { x: ev.clientX - r.left, y: ev.clientY - r.top };
      const box = { x: Math.min(start.x, cur.x), y: Math.min(start.y, cur.y), w: Math.abs(cur.x - start.x), h: Math.abs(cur.y - start.y) };
      setMarquee(box);
      if (box.w < 3 && box.h < 3) return;
      const hit = [...turfRef.current.querySelectorAll("[data-sel-id]")].filter((el) => {
        const b = el.getBoundingClientRect();
        return b.left - r.left < box.x + box.w && b.right - r.left > box.x && b.top - r.top < box.y + box.h && b.bottom - r.top > box.y;
      }).map((el) => el.dataset.selId);
      setSelectedIds([...new Set([...base, ...hit])]);
    };
    const up = () => { setMarquee(null); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const bg = design.base_style || { pattern: "solid", primary: design.turf_colour_hex };
  const art = design.ai_artwork;
  const pxPerM = turfW / L;
  const bw = design.border?.enabled ? (design.border.width_m || 0) * pxPerM : 0;
  // One reusable turf-material filter per canvas: fibres clipped to each element's own pixels (manual mode only).
  const material = art ? undefined : `url(#${matId})`;

  return (
    <div ref={wrapRef} className="relative w-full h-full flex items-center justify-center overflow-hidden"
      onPointerDown={() => !readOnly && setSelectedId && setSelectedId(null)} data-testid="turf-canvas-wrap">
      {!art && <div className="absolute font-display font-bold text-pgt-green tracking-widest" style={{ top: `calc(50% - ${turfH / 2}px - 28px)`, fontSize: 14 }} data-testid="ruler-length">{L.toFixed(1)}M</div>}
      {!art && <div className="absolute font-display font-bold text-pgt-green tracking-widest" style={{ left: `calc(50% - ${turfW / 2}px - 42px)`, transform: "rotate(-90deg)", fontSize: 14 }} data-testid="ruler-width">{W.toFixed(1)}M</div>}

      <div ref={turfRef} className="relative shadow-2xl overflow-hidden" data-testid="turf-surface"
        style={{ width: turfW, height: turfH, background: bg.primary || "#111", outline: "1px solid rgba(255,255,255,0.15)", touchAction: "none", userSelect: "none", WebkitUserSelect: "none", caretColor: "transparent" }}
        onPointerDown={(e) => { e.stopPropagation(); if (!readOnly) { e.preventDefault(); clearCaret(); startMarquee(e); } }} onMouseDown={readOnly ? undefined : noCaret}>
        {marquee && <div data-testid="marquee-box" className="absolute z-[60] pointer-events-none border border-[#00D26A] bg-[#00D26A]/15" style={{ left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h }} />}
        {art ? (
          // AI MODE: GPT Image 2 renders the COMPLETE finished artwork (markings, numbers, border, lanes
          // and all graphics). The deterministic renderer must NOT add any marking/track layer here.
          <img src={art.url} alt="AI artwork" draggable={false} className="absolute inset-0 w-full h-full object-contain z-0 select-none pointer-events-none" data-testid="ai-artwork-layer" />
        ) : (
          <>
            <div className="absolute inset-0 z-0" dangerouslySetInnerHTML={{ __html: buildBaseSvg(bg, L, W) }} />
            <div className="absolute inset-0 pointer-events-none z-10" data-testid="turf-texture-layer" dangerouslySetInnerHTML={{ __html: buildTurfTextureSvg(L, W) }} />
            <div data-testid="turf-material-filter" dangerouslySetInnerHTML={{ __html: turfMaterialFilterSvg(matId, pxPerM) }} />
            {bw > 0 && <div className="absolute inset-0 pointer-events-none z-[15]" style={{ border: `${bw}px solid ${design.border.colour}`, boxSizing: "border-box", filter: material }} data-testid="turf-border" />}
            <TrackLayer design={design} W={turfW} H={turfH} material={material} selectedIds={selSet} setSelectedId={setSelectedId} toggleSelected={toggleSelected} readOnly={readOnly} />
          </>
        )}
        {guides.v && <div className="absolute top-0 bottom-0 pointer-events-none z-40" style={{ left: "50%", width: 1, background: "#00D26A" }} />}
        {guides.h && <div className="absolute left-0 right-0 pointer-events-none z-40" style={{ top: "50%", height: 1, background: "#00D26A" }} />}

        {design.objects.map((o) => {
          const d = dims(o);
          const selected = selSet.includes(o.id);
          return (
            <div key={o.id} className="absolute" data-testid={`obj-${o.id}`} data-sel-id={o.id}
              style={{ left: o.x * turfW - d.w / 2, top: o.y * turfH - d.h / 2, width: d.w, height: d.h, zIndex: selected ? 50 : 40, transform: `rotate(${o.rotation || 0}deg)`, containerType: "size", cursor: readOnly ? "default" : "move", outline: readOnly ? "none" : selected ? "2px solid #00D26A" : "none", outlineOffset: 2, touchAction: "none" }}
              onPointerDown={(e) => onDown(e, o, "drag")} onMouseDown={noCaret}>
              <div className="absolute inset-0" style={{ filter: material }} data-testid={`obj-material-${o.id}`}><ObjectContent obj={o} /></div>
              {selected && !readOnly && <div data-testid={`resize-${o.id}`} onPointerDown={(e) => onDown(e, o, "resize")} className="absolute -right-1.5 -bottom-1.5 w-3.5 h-3.5 bg-pgt-green rounded-sm cursor-nwse-resize" style={{ touchAction: "none" }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
